import type {
  CustomField,
  SimpleField,
  Transaction,
} from '@/components/DataReflection/TransactionDetailSection/types';
import {
  DEFAULT_FIELDS,
  TAX_RELATED_DEFAULT_FIELDS,
} from '@/features/InvoiceReport/constants';
import type { TransactionProperty } from '@/features/InvoiceReport/Edit/type';
import { toCommaSeparated } from '@/utils/convert/toCommaSeparated';
import { getInvoiceKindLabel } from '@/utils/InvoiceKindLabelMapping';
import type { InvoiceTransactionDetail } from 'ap-openapi';
import type { TFunction } from 'i18next';

function getCustomTransactionItemValue(
  transaction: Transaction,
  propertyId: string
): string {
  const raw = transaction['custom_transaction_items'];
  if (raw === null || raw === undefined || typeof raw !== 'object') {
    return '';
  }
  if (!Object.prototype.hasOwnProperty.call(raw, propertyId)) {
    return '';
  }
  const value = Reflect.get(raw, propertyId);
  return value === null || value === undefined ? '' : String(value);
}

/**
 * Maps a custom pulldown’s stored option id to the display string (same as CSV/export).
 * Wire with `findCustomPulldownOptionById` from `useAggregatedCustomPulldownItems` to reuse the React Query cache.
 */
export type ResolvePulldownDisplayName = (
  propertyId: string,
  storedValue: string
) => string;

/** Pulldown: maps stored option id to display string when a resolver is provided. */
function getCustomTransactionItemDisplayValue(
  transaction: Transaction,
  property: TransactionProperty,
  resolvePulldownDisplayName?: ResolvePulldownDisplayName
): string {
  const raw = getCustomTransactionItemValue(transaction, property.id);
  if (
    property.componentType === 'pulldown' &&
    resolvePulldownDisplayName !== undefined
  ) {
    return resolvePulldownDisplayName(property.id, raw);
  }
  return raw;
}

export type BuildDetailFieldsOptions = {
  transactionProperties: TransactionProperty[] | undefined;
  t: TFunction;
  resolvePulldownDisplayName?: ResolvePulldownDisplayName;
};

function createDetailComputes(
  t: TFunction
): Record<string, (tx: InvoiceTransactionDetail) => string> {
  return {
    // Deal date is intentionally left blank in the past-details drawer.
    [DEFAULT_FIELDS.DEAL_DATE]: () => '',
    [DEFAULT_FIELDS.NAME]: (tx) => tx.name ?? '',
    [DEFAULT_FIELDS.DEPT]: (tx) => tx.dept?.name ?? '',
    [DEFAULT_FIELDS.EX_ITEM]: (tx) => tx.ex_item?.name ?? '',
    [DEFAULT_FIELDS.QUANTITY]: (tx) =>
      tx.quantity !== undefined ? String(tx.quantity) : '',
    [DEFAULT_FIELDS.UNIT]: (tx) =>
      tx.unit_value !== undefined ? toCommaSeparated(tx.unit_value) : '',
    [DEFAULT_FIELDS.AMOUNT]: (tx) =>
      tx.total_value !== undefined ? toCommaSeparated(tx.total_value) : '',
    [DEFAULT_FIELDS.TAX_CATEGORY]: (tx) => tx.dr_excise?.name ?? '',
    [DEFAULT_FIELDS.INVOICE_KIND]: (tx) =>
      getInvoiceKindLabel(t, tx.invoice_kind),
    [DEFAULT_FIELDS.TAX_AMOUNT]: (tx) =>
      tx.excise_value !== undefined ? toCommaSeparated(tx.excise_value) : '',
    [DEFAULT_FIELDS.WITHHOLDING_TAX_CHECK]: (tx) =>
      tx.has_withholding_income_tax === undefined
        ? ''
        : tx.has_withholding_income_tax
        ? '○'
        : '',
    [DEFAULT_FIELDS.WITHHOLDING_TAX_AMOUNT]: (tx) =>
      tx.has_withholding_income_tax &&
      tx.withholding_income_tax_value !== undefined
        ? toCommaSeparated(tx.withholding_income_tax_value)
        : '',
    [DEFAULT_FIELDS.MEMO]: (tx) => tx.memo ?? '',
    [DEFAULT_FIELDS.PROJECT]: (tx) => tx.project_code?.name ?? '',
    [DEFAULT_FIELDS.CR_ITEM]: (tx) => tx.cr_item?.name ?? '',
    [DEFAULT_FIELDS.CR_SUB_ITEM]: (tx) => tx.cr_sub_item?.name ?? '',
    [DEFAULT_FIELDS.CURRENCY]: (tx) => tx.currency ?? '',
    [DEFAULT_FIELDS.JPY_RATE]: (tx) =>
      tx.jpyrate !== undefined ? toCommaSeparated(tx.jpyrate) : '',
  };
}

function getDefaultFieldLabel(t: TFunction, masterFieldName: string): string {
  const label = t(`invoice_transaction.column.${masterFieldName}`);

  // NOTE: Always display in tax-excluded format, because the transaction's total value is always tax-excluded.
  if (TAX_RELATED_DEFAULT_FIELDS.includes(masterFieldName)) {
    return label + t(`invoice_transaction.column.tax_exclude`);
  }

  return label;
}

/**
 * Builds detail drawer fields from `transactionProperties`.
 * Expects `transactionProperties` to be pre-filtered by `useVisibleTransactionProperties`
 * so that no additional visibility checks are needed here.
 */
export function buildDetailFieldsFromTransactionProperties(
  opts: BuildDetailFieldsOptions
): CustomField[] {
  const { transactionProperties, t, resolvePulldownDisplayName } = opts;

  if (!transactionProperties?.length) {
    return buildFallbackDetailFields(t);
  }

  const fields: CustomField[] = [];

  for (const property of transactionProperties) {
    if (property.type === 'custom') {
      fields.push({
        key: property.id,
        label: property.label,
        compute: (transaction) =>
          getCustomTransactionItemDisplayValue(
            transaction,
            property,
            resolvePulldownDisplayName
          ),
        componentType: property.componentType,
      });
      continue;
    }

    const masterFieldName = property.masterFieldName;
    const computes = createDetailComputes(t);
    const compute = computes[masterFieldName];
    const label = getDefaultFieldLabel(t, masterFieldName);

    if (compute) {
      fields.push({
        key: masterFieldName,
        label,
        compute,
      });
    } else {
      fields.push({
        key: masterFieldName,
        label,
      });
    }
  }

  return fields;
}

/** Subset order preserved from the original static drawer fields. */
function buildFallbackDetailFields(t: TFunction): SimpleField[] {
  const label = (name: string) => getDefaultFieldLabel(t, name);
  const computes = createDetailComputes(t);

  /* NOTE:
   * There is no invoiceKind, unit, quantity, memo in the fallback detail fields.
   * This follows the approach of the previous implementation, which displayed the details in a simplified manner.
   */

  return [
    {
      key: DEFAULT_FIELDS.DEAL_DATE,
      label: label(DEFAULT_FIELDS.DEAL_DATE),
      compute: computes[DEFAULT_FIELDS.DEAL_DATE],
    },
    { key: DEFAULT_FIELDS.NAME, label: label(DEFAULT_FIELDS.NAME) },
    {
      key: DEFAULT_FIELDS.DEPT,
      label: label(DEFAULT_FIELDS.DEPT),
      compute: computes[DEFAULT_FIELDS.DEPT],
    },
    {
      key: DEFAULT_FIELDS.EX_ITEM,
      label: label(DEFAULT_FIELDS.EX_ITEM),
      compute: computes[DEFAULT_FIELDS.EX_ITEM],
    },
    {
      key: DEFAULT_FIELDS.AMOUNT,
      label: label(DEFAULT_FIELDS.AMOUNT),
      compute: computes[DEFAULT_FIELDS.AMOUNT],
    },
    {
      key: DEFAULT_FIELDS.TAX_CATEGORY,
      label: label(DEFAULT_FIELDS.TAX_CATEGORY),
      compute: computes[DEFAULT_FIELDS.TAX_CATEGORY],
    },
    {
      key: DEFAULT_FIELDS.TAX_AMOUNT,
      label: label(DEFAULT_FIELDS.TAX_AMOUNT),
      compute: computes[DEFAULT_FIELDS.TAX_AMOUNT],
    },
    {
      key: DEFAULT_FIELDS.WITHHOLDING_TAX_AMOUNT,
      label: label(DEFAULT_FIELDS.WITHHOLDING_TAX_AMOUNT),
      compute: computes[DEFAULT_FIELDS.WITHHOLDING_TAX_AMOUNT],
    },
    {
      key: DEFAULT_FIELDS.PROJECT,
      label: label(DEFAULT_FIELDS.PROJECT),
      compute: computes[DEFAULT_FIELDS.PROJECT],
    },
    {
      key: DEFAULT_FIELDS.CR_ITEM,
      label: label(DEFAULT_FIELDS.CR_ITEM),
      compute: computes[DEFAULT_FIELDS.CR_ITEM],
    },
    {
      key: DEFAULT_FIELDS.CR_SUB_ITEM,
      label: label(DEFAULT_FIELDS.CR_SUB_ITEM),
      compute: computes[DEFAULT_FIELDS.CR_SUB_ITEM],
    },
    {
      key: DEFAULT_FIELDS.CURRENCY,
      label: label(DEFAULT_FIELDS.CURRENCY),
      compute: computes[DEFAULT_FIELDS.CURRENCY],
    },
  ];
}
